import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Linkedin, CalendarClock, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { PROFILE } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  {
    key: "mail",
    label: "Mail",
    href: `mailto:${PROFILE.email}`,
    icon: Mail,
    color: "#EA4335",
    tint: "rgba(234,67,53,0.10)",
    external: false,
    testid: "contact-email-link",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    href: PROFILE.linkedin,
    icon: Linkedin,
    color: "#0A66C2",
    tint: "rgba(10,102,194,0.10)",
    external: true,
    testid: "contact-linkedin",
  },
  {
    key: "meeting",
    label: "Book a meeting",
    href: PROFILE.calendly,
    icon: CalendarClock,
    color: "#006BFF",
    tint: "rgba(0,107,255,0.10)",
    external: true,
    testid: "contact-calendly",
  },
];

function ChannelButton({ c }) {
  const [hover, setHover] = useState(false);
  const Icon = c.icon;
  return (
    <a
      data-testid={c.testid}
      href={c.href}
      target={c.external ? "_blank" : undefined}
      rel={c.external ? "noreferrer" : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group flex items-center gap-3 rounded-full border bg-card pl-2 pr-5 py-2 transition-all duration-300 hover:-translate-y-0.5"
      style={{
        borderColor: hover ? c.color : "rgba(23,23,26,0.1)",
        backgroundColor: hover ? c.tint : "var(--card)",
      }}
    >
      <span className="w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0" style={{ backgroundColor: c.color }}>
        <Icon size={17} />
      </span>
      <span className="font-display font-medium text-[15px]">{c.label}</span>
      <ArrowUpRight size={16} className="text-ink-soft transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </a>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || "Message sent!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Try emailing me directly.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section id="contact" data-testid="contact-section" className="relative py-14 md:py-20 px-4 md:px-6">
      <div className="max-w-[1240px] mx-auto">
        <div className="card p-8 md:p-14 grid md:grid-cols-2 gap-10 md:gap-16">
          <div>
            <Reveal>
              <p className="font-display text-accent text-sm mb-3">Contact</p>
              <h2 className="font-display font-semibold text-4xl md:text-5xl leading-[1.05] tracking-tight">
                Let's build something great together.
              </h2>
              <p className="text-ink-soft mt-5 leading-relaxed max-w-md">
                Have a product challenge, a role, or an idea worth exploring? Drop me a note and I'll get back to you soon.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-9 flex flex-wrap gap-3">
                {CHANNELS.map((c) => (
                  <ChannelButton key={c.key} c={c} />
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <form data-testid="contact-form" onSubmit={submit} className="space-y-7">
              <div>
                <label className="font-display text-sm text-ink-soft">Name</label>
                <input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your name" className="fluted mt-1" />
              </div>
              <div>
                <label className="font-display text-sm text-ink-soft">Email</label>
                <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className="fluted mt-1" />
              </div>
              <div>
                <label className="font-display text-sm text-ink-soft">Message</label>
                <textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={3} placeholder="Tell me about your product…" className="fluted mt-1 resize-none" />
              </div>
              <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-dark disabled:opacity-50">
                {loading ? "Sending…" : "Send message"}
                <span aria-hidden>→</span>
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
